import _ from 'lodash';
import React from 'react';
import { decorate, computed, action, observable, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Modal, Icon, Popup } from 'semantic-ui-react';

import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import { displayError } from '@amzn/base-ui/dist/helpers/notification';

import ScEnvironmentConnections from './ScEnvironmentConnections';
import ScEnvironmentUpdateCidrs from './ScEnvironmentUpdateCidrs';
import ChangeInstanceTypeModal from './ChangeInstanceTypeModal';
import ModifyStorageSizeModal from './ModifyStorageSizeModal';
import WorkspaceSchedulingModal from './WorkspaceSchedulingModal';
import {
  enableEgressStore,
  enableWorkspaceScheduler,
  isAppStreamEnabled,
  restrictAdminWorkspaceConnection,
} from '../../../helpers/settings';
import {
  getScCompatibleInstanceTypes,
  getScStorageInfo,
  getScSchedulingInfo,
  getScIdleActivity,
} from '../../../helpers/api';
import ScEnvironmentEgressStoreDetail from './ScEnvironmentEgressStoreDetail';

const PROCESSING_STATUS_CODE = 'PROCESSING';
const WORKSPACE_TERMINATION_ERROR_MESSAGE =
  'Termination is NOT allowed. Your data transfer from egress store is still in progress. Please contact your Data Manager to confirm the data is transferred before you try to terminate the workspace again.';

// expected props
// - scEnvironment (via prop)
// - showDetailButton (via prop)
// - compactMode (via prop, optional)
// - scEnvironmentsStore (via injection)
// - envTypesStore (via injection)
class ScEnvironmentButtons extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      // A flag to indicate if we are processing the call to trigger the terminate action
      this.processing = false;
      // A flag to indicate if the connections button is active
      this.connectionsButtonActive = false;
      // A flag to indicate if the cidr edit button is active
      this.editCidrButtonActive = false;
      // A flag to indicate if the egressStore button is active
      this.egressStoreButtonActive = false;
      // A flag to indicate if the terminate modal is open
      this.terminateModalOpen = false;
      // A flag to indicate if the change instance type modal is open
      this.changeInstanceTypeModalOpen = false;
      // A flag to indicate if the modify storage size modal is open
      this.modifyStorageSizeModalOpen = false;
      // A flag to indicate if the scheduling modal is open
      this.schedulingModalOpen = false;
      // A flag to indicate if the actions dropdown is open
      this.actionsDropdownOpen = false;
    });
  }

  get userStore() {
    return this.props.userStore;
  }

  get envTypesStore() {
    return this.props.envTypesStore;
  }

  get envType() {
    const env = this.props.scEnvironment;
    const store = this.envTypesStore;
    const envType = store.getEnvType(env.envTypeId);

    return envType;
  }

  get environment() {
    return this.props.scEnvironment;
  }

  get envsStore() {
    return this.props.scEnvironmentsStore;
  }

  get hasCidrParam() {
    const envType = this.envTypesStore.getEnvType(this.environment.envTypeId);
    return _.some(_.get(envType, 'params', []), (p) => p.ParameterKey === 'AccessFromCIDRBlock');
  }

  // Whether the Scheduling action is offered for this workspace (Requirements 3.3 and 3.5).
  //
  // `canChangeState()` tests for an `Ec2WorkspaceInstanceId` or a `NotebookInstanceName` in the stack
  // outputs, and `isSageMakerWorkspace()` excludes the notebook case; together they give the
  // Eligible_Workspace test, which is an EC2 instance the tags can be written to. The per-status
  // capability flag keeps the item out of transitional states, and the feature flag keeps it out of a
  // deployment that has not enabled the scheduler (Requirement 1.4).
  //
  // Read as a computed rather than threaded through as another argument, so both the full and the
  // compact render path see the same value without the condition being restated (Requirement 16.7).
  get canSetSchedule() {
    return (
      enableWorkspaceScheduler &&
      this.environment.state.canSetSchedule &&
      this.canChangeState() &&
      !this.isSageMakerWorkspace()
    );
  }

  handleViewDetail = () => {
    const goto = gotoFn(this);
    goto(`/workspaces/id/${this.environment.id}`);
  };

  getEgressStoreDetailsStore = () => {
    if (enableEgressStore) return this.props.scEnvironmentEgressStoreDetailStore;
    return null;
  };

  handleTerminate = async () => {
    if (enableEgressStore) {
      const egressStoreDetailsStore = this.getEgressStoreDetailsStore();
      if (!egressStoreDetailsStore) {
        await this.handleAction(async () => {
          const store = this.envsStore;
          await store.terminateScEnvironment(this.environment.id);
          this.setTerminateModalState(false);
        });
      } else {
        const isDataEgressing =
          egressStoreDetailsStore.egressStoreStatus.toLowerCase() === PROCESSING_STATUS_CODE.toLowerCase();
        if (!isDataEgressing) {
          await this.handleAction(async () => {
            const store = this.envsStore;
            await store.terminateScEnvironment(this.environment.id);
            this.setTerminateModalState(false);
          });
        } else {
          // Do not allow user to terminate workspace if the data egressing is in process
          displayError(WORKSPACE_TERMINATION_ERROR_MESSAGE);
        }
      }
    } else {
      await this.handleAction(async () => {
        const store = this.envsStore;
        await store.terminateScEnvironment(this.environment.id);
        this.setTerminateModalState(false);
      });
    }
  };

  handleRestart = async () => {
    await this.handleAction(async () => {
      const store = this.envsStore;
      await store.restartScEnvironment(this.environment.id);
    });
  };

  handleStop = async () => {
    await this.handleAction(async () => {
      const store = this.envsStore;
      await store.stopScEnvironment(this.environment.id);
    });
  };

  handleStart = async () => {
    // Clear capacity error from UI immediately when user retries
    this.environment.setScEnvironment({ ...this.environment, capacityError: false });
    await this.handleAction(async () => {
      const store = this.envsStore;
      await store.startScEnvironment(this.environment.id);
    });
  };

  setTerminateModalState = async (state) => {
    runInAction(() => {
      this.terminateModalOpen = state;
    });
  };

  setChangeInstanceTypeModalOpen = (state) => {
    runInAction(() => {
      this.changeInstanceTypeModalOpen = state;
    });
  };

  setModifyStorageSizeModalOpen = (state) => {
    runInAction(() => {
      this.modifyStorageSizeModalOpen = state;
    });
  };

  setSchedulingModalOpen = state => {
    runInAction(() => {
      this.schedulingModalOpen = state;
    });
  };

  setActionsDropdownOpen = (state) => {
    runInAction(() => {
      this.actionsDropdownOpen = state;
    });
  };

  handleChangeInstanceTypeConfirm = async (selectedConfigId, enableHyperthreading) => {
    const store = this.envsStore;
    await store.changeInstanceType(this.environment.id, selectedConfigId, enableHyperthreading);
  };

  handleModifyStorageSizeConfirm = async (targetSizeGB) => {
    const store = this.envsStore;
    await store.modifyStorageSize(this.environment.id, targetSizeGB);
  };

  // The two scheduling writes deliberately do not go through `handleAction`. That helper sets the
  // shared `processing` flag, which disables the Start, Stop, Restart and Terminate buttons, and a tag
  // write changes nothing about the workspace's lifecycle. It also swallows the error into a toast,
  // whereas the modal needs the rejection so it can stay open, show the message and keep the user's
  // entered values. The modal owns its own submitting state.
  handleSaveScheduleConfirm = async scheduleValue => {
    const store = this.envsStore;
    return store.setSchedule(this.environment.id, scheduleValue);
  };

  handleSaveIdleStopConfirm = async (idleStopMinutes, cpuThreshold) => {
    const store = this.envsStore;
    return store.setIdleStop(this.environment.id, idleStopMinutes, cpuThreshold);
  };

  canChangeState() {
    return this.envsStore.canChangeState(this.environment.id);
  }

  handleAction = async fn => {
    this.processing = true;
    try {
      await fn();
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => {
        this.processing = false;
      });
    }
  };

  handleToggle = () => {
    this.connectionsButtonActive = !this.connectionsButtonActive;
    // Close the CIDR segment if open - RACE
    if (this.editCidrButtonActive && this.connectionsButtonActive) {
      this.editCidrButtonActive = false;
    }
  };

  handleCidrEditToggle = () => {
    this.editCidrButtonActive = !this.editCidrButtonActive;
    // Close the connections segment if open - RACE
    if (this.connectionsButtonActive && this.editCidrButtonActive) {
      this.connectionsButtonActive = false;
    }
  };

  handleEgressStoreToggle = () => {
    this.egressStoreButtonActive = !this.egressStoreButtonActive;
  };

  isSageMakerWorkspace = () => {
    const envType = this.envType;
    const osKeywords = ['windows', 'ubuntu', 'linux', 'redhat', 'rhel'];
    const envName = (envType?.name || '').toLowerCase();
    return envName.includes('jupyterlab') && !osKeywords.some(keyword => envName.includes(keyword));
  };

  handleSetScheduleClick = () => {
    this.setActionsDropdownOpen(false);
    this.setSchedulingModalOpen(true);
  };

  // The Scheduling dropdown item, written once and rendered by both the full and the compact path. Only
  // the test id differs between them (Requirement 16.7).
  renderSetScheduleDropdownItem(testId) {
    return (
      <button
        type="button"
        className="workspace-actions-dropdown-item"
        data-testid={testId}
        onClick={this.handleSetScheduleClick}
      >
        <Icon name="calendar alternate outline" style={{ lineHeight: 1, height: '1em', margin: 0, color: '#2563eb' }} />
        {/* Verb then thing, like Change Instance Type and Modify Storage Size beside it. */}
        Configure Auto Start/Stop
      </button>
    );
  }

  // Mounted once by whichever render path is active, in the same place as the other two workspace
  // modals. The API reader is passed in as a prop, so the modal can be rendered in a test without a
  // network stub.
  renderSchedulingModal(env) {
    return (
      <WorkspaceSchedulingModal
        open={this.schedulingModalOpen}
        onClose={() => this.setSchedulingModalOpen(false)}
        environment={env}
        getSchedulingInfo={getScSchedulingInfo}
        getIdleActivity={getScIdleActivity}
        onSaveSchedule={this.handleSaveScheduleConfirm}
        onSaveIdleStop={this.handleSaveIdleStopConfirm}
      />
    );
  }

  render() {
    const env = this.environment;
    const state = env.state;
    const processing = this.processing;
    const showDetailButton = this.props.showDetailButton;
    const compactMode = this.props.compactMode;
    const connectionsButtonActive = this.connectionsButtonActive;
    const egressStoreButtonActive = this.egressStoreButtonActive;
    const editCidrButtonActive = this.editCidrButtonActive;
    let canConnect = state.canConnect;
    const canStart = state.canStart && this.canChangeState();
    const canStop = state.canStop && this.canChangeState();
    const canRestart = state.canStop && this.canChangeState() && !this.isSageMakerWorkspace();
    const canChangeInstanceType = state.canChangeInstanceType && this.canChangeState();
    const canModifyStorageSize = state.canModifyStorageSize && this.canChangeState();
    const canSetSchedule = this.canSetSchedule;

    const envCreatedBy = env.createdBy;
    const user = this.userStore.user;
    const isAdmin = user.isAdmin;
    const uid = user.uid;

    //  Validate the flag "restrictAdminWorkspaceConnection" if it set to "true"
    //  Restrict the admin to access workspaces only created by them.
    if (restrictAdminWorkspaceConnection === true) {
      if (isAdmin === true) {
        canConnect = false;
      }
      if (uid === envCreatedBy) {
        canConnect = true;
      }
    }

    // Compact mode - icon-only buttons
    if (compactMode) {
      // The capability flags are passed as a single object rather than a long positional list, so a
      // reader of either the call site or the signature can see which flag is which (Requirement 16.7).
      return this.renderCompact({
        env,
        state,
        processing,
        canConnect,
        canStart,
        canStop,
        canRestart,
        canChangeInstanceType,
        canModifyStorageSize,
        showDetailButton,
        connectionsButtonActive,
        editCidrButtonActive,
        egressStoreButtonActive,
      });
    }

    return (
      <>
        <div className="workspace-card-buttons">
          {/* Left side buttons */}
          <div className="workspace-card-buttons-left">
            {canConnect && !this.props.hideConnections && (!isAppStreamEnabled || env.isAppStreamConfigured) && (
              <Popup
                trigger={
                  <button
                    className={`workspace-action-btn workspace-action-btn-primary ${connectionsButtonActive ? 'active' : ''}`}
                    onClick={this.handleToggle}
                    data-testid="sc-environment-connection-button"
                  >
                    <Icon name="plug" style={{ lineHeight: 1, height: '1em', margin: 0 }} /> Connections
                  </button>
                }
                content="View and manage connection options to access your workspace"
                size="small"
                position="top center"
                mouseEnterDelay={500}
                inverted
              />
            )}
            {!isAppStreamEnabled && this.hasCidrParam && state.canTerminate && !state.key.includes('FAILED') && (
              <Popup
                trigger={
                  <button
                    className={`workspace-action-btn workspace-action-btn-secondary ${editCidrButtonActive ? 'active' : ''}`}
                    onClick={this.handleCidrEditToggle}
                  >
                    <Icon name="shield" style={{ lineHeight: 1, height: '1em', margin: 0 }} /> Manage Security Group
                  </button>
                }
                content="Configure which IP addresses can connect to your workspace"
                size="small"
                position="top center"
                mouseEnterDelay={500}
                inverted
              />
            )}
            {showDetailButton && (
              <Popup
                trigger={
                  <button
                    className="workspace-action-btn workspace-action-btn-secondary"
                    onClick={this.handleViewDetail}
                  >
                    <Icon name="info circle" style={{ lineHeight: 1, height: '1em', margin: 0 }} /> More Details & Cost
                  </button>
                }
                content="View detailed workspace information, configuration, and cost history"
                size="small"
                position="top center"
                mouseEnterDelay={500}
                inverted
              />
            )}
            {enableEgressStore && state.canTerminate && !state.key.includes('FAILED') && (
              <Popup
                trigger={
                  <button
                    className={`workspace-action-btn workspace-action-btn-secondary ${egressStoreButtonActive ? 'active' : ''}`}
                    onClick={this.handleEgressStoreToggle}
                  >
                    <Icon name="external share" style={{ lineHeight: 1, height: '1em', margin: 0 }} /> Egress Store
                  </button>
                }
                content="Manage data egress and transfer files out of your workspace"
                size="small"
                position="top center"
                mouseEnterDelay={500}
                inverted
              />
            )}
          </div>

          {/* Right side buttons */}
          <div className="workspace-card-buttons-right">
            {(canChangeInstanceType || canModifyStorageSize || canSetSchedule) && (
              <div className="workspace-actions-dropdown-wrapper">
                <button
                  className="workspace-action-btn workspace-action-btn-secondary"
                  data-testid="sc-env-actions-dropdown"
                  disabled={processing}
                  onClick={(e) => { e.stopPropagation(); this.setActionsDropdownOpen(!this.actionsDropdownOpen); }}
                >
                  <Icon name="setting" style={{ lineHeight: 1, height: '1em', margin: 0 }} /> Actions <Icon name="caret down" style={{ lineHeight: 1, height: '1em', margin: '0 0 0 2px' }} />
                </button>
                {this.actionsDropdownOpen && (
                  <>
                    <div className="workspace-actions-dropdown-backdrop" onClick={() => this.setActionsDropdownOpen(false)} />
                    <div className="workspace-actions-dropdown-menu">
                      {canChangeInstanceType && (
                        <button
                          className="workspace-actions-dropdown-item"
                          data-testid="sc-env-change-instance-type"
                          onClick={() => { this.setActionsDropdownOpen(false); this.setChangeInstanceTypeModalOpen(true); }}
                        >
                          <Icon name="exchange" style={{ lineHeight: 1, height: '1em', margin: 0, color: '#2563eb' }} />
                          Change Instance Type
                        </button>
                      )}
                      {canModifyStorageSize && (
                        <button
                          className="workspace-actions-dropdown-item"
                          data-testid="sc-env-modify-storage-size"
                          onClick={() => { this.setActionsDropdownOpen(false); this.setModifyStorageSizeModalOpen(true); }}
                        >
                          <Icon name="hdd" style={{ lineHeight: 1, height: '1em', margin: 0, color: '#2563eb' }} />
                          Modify Storage Size
                        </button>
                      )}
                      {canSetSchedule && this.renderSetScheduleDropdownItem('sc-env-set-schedule')}
                    </div>
                  </>
                )}
              </div>
            )}
            {canStart && (
              <Popup
                trigger={
                  <button
                    data-testid="sc-env-start"
                    className="workspace-action-btn workspace-action-btn-start"
                    onClick={this.handleStart}
                    disabled={processing}
                  >
                    {processing ? <Icon name="spinner" loading style={{ lineHeight: 1, height: '1em', margin: 0 }} /> : <Icon name="play" style={{ lineHeight: 1, height: '1em', margin: 0 }} />} Start
                  </button>
                }
                content="Start the workspace to resume working. This may take a few minutes."
                size="small"
                position="top center"
                mouseEnterDelay={500}
                inverted
              />
            )}
            {canRestart && (
              <Popup
                trigger={
                  <button
                    data-testid="sc-env-restart"
                    className="workspace-action-btn workspace-action-btn-restart"
                    onClick={this.handleRestart}
                    disabled={processing}
                  >
                    {processing ? <Icon name="spinner" loading style={{ lineHeight: 1, height: '1em', margin: 0 }} /> : <Icon name="redo" style={{ lineHeight: 1, height: '1em', margin: 0 }} />} Restart
                  </button>
                }
                content="Restart the workspace. Useful if the workspace is unresponsive."
                size="small"
                position="top center"
                mouseEnterDelay={500}
                inverted
              />
            )}
            {canStop && (
              <Popup
                trigger={
                  <button
                    data-testid="sc-env-stop"
                    className="workspace-action-btn workspace-action-btn-stop"
                    onClick={this.handleStop}
                    disabled={processing}
                  >
                    {processing ? <Icon name="spinner" loading style={{ lineHeight: 1, height: '1em', margin: 0 }} /> : <Icon name="stop" style={{ lineHeight: 1, height: '1em', margin: 0 }} />} Stop
                  </button>
                }
                content="Stop the workspace to save costs. Your data will be preserved."
                size="small"
                position="top center"
                mouseEnterDelay={500}
                inverted
              />
            )}
            {state.canTerminate &&
              (this.environment.terminationLocked ? (
                <Popup
                  trigger={
                    <button
                      disabled
                      data-testid="sc-env-terminate"
                      className="workspace-action-btn workspace-action-btn-terminate"
                    >
                      <Icon name="lock" style={{ lineHeight: 1, height: '1em', margin: 0 }} /> Terminate
                    </button>
                  }
                  content="This workspace is locked and cannot be terminated. Contact support for assistance."
                  size="small"
                  position="top center"
                  mouseEnterDelay={500}
                  inverted
                />
              ) : (
                <Modal
                  dimmer='blurring'
                  basic
                  size='large'
                  open={this.terminateModalOpen}
                  onClose={() => this.setTerminateModalState(false)}
                  onOpen={() => this.setTerminateModalState(true)}
                  trigger={
                    <button
                      data-testid="sc-env-terminate"
                      className="workspace-action-btn workspace-action-btn-terminate"
                      disabled={processing}
                      title="Permanently delete this workspace and all its data"
                    >
                      {processing ? <Icon name="spinner" loading style={{ lineHeight: 1, height: '1em', margin: 0 }} /> : <Icon name="trash alternate" style={{ lineHeight: 1, height: '1em', margin: 0 }} />} Terminate
                    </button>
                  }
                  header={
                    <div className="workspace-modal-header">
                      <Icon name='warning sign' color='red' size='massive' />
                      <div className="workspace-modal-title">
                        <span>Proceed with caution!</span>
                      </div>
                    </div>
                  }
                  content={
                    <div className="workspace-modal-content">
                      Performing this action will permanently erase your workspace and all related data, which cannot be restored.
                      Any data within a study will be retained. Are you sure you want to proceed?
                    </div>
                  }
                  actions={
                    <div className="workspace-modal-actions">
                      <Modal.Actions>
                        <button className="workspace-action-btn workspace-action-btn-start" onClick={() => this.setTerminateModalState(false)}>
                          Cancel
                        </button>
                        <button className="workspace-action-btn workspace-action-btn-terminate" onClick={this.handleTerminate}>
                          Terminate
                        </button>
                      </Modal.Actions>
                    </div>
                  }
                />
              ))}
          </div>
        </div>
        {enableEgressStore && egressStoreButtonActive && <ScEnvironmentEgressStoreDetail scEnvironment={env} />}
        {canConnect && !this.props.hideConnections && connectionsButtonActive && <ScEnvironmentConnections scEnvironment={env} config={this.props.config} />}
        {editCidrButtonActive && <ScEnvironmentUpdateCidrs scEnvironment={env} onCancel={this.handleCidrEditToggle} />}
        <ChangeInstanceTypeModal
          open={this.changeInstanceTypeModalOpen}
          onClose={() => this.setChangeInstanceTypeModalOpen(false)}
          environment={env}
          onConfirm={this.handleChangeInstanceTypeConfirm}
          getCompatibleInstanceTypes={getScCompatibleInstanceTypes}
        />
        <ModifyStorageSizeModal
          open={this.modifyStorageSizeModalOpen}
          onClose={() => this.setModifyStorageSizeModalOpen(false)}
          environment={env}
          onConfirm={this.handleModifyStorageSizeConfirm}
          getStorageInfo={getScStorageInfo}
        />
        {this.renderSchedulingModal(env)}
      </>
    );
  }

  renderCompact({
    env,
    state,
    processing,
    canConnect,
    canStart,
    canStop,
    canRestart,
    canChangeInstanceType,
    canModifyStorageSize,
    showDetailButton,
    connectionsButtonActive,
    editCidrButtonActive,
    egressStoreButtonActive,
  }) {
    const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };
    // Read from the computed rather than taken as another positional argument, so the eligibility test
    // is stated once for both render paths.
    const canSetSchedule = this.canSetSchedule;

    return (
      <>
        <div className="workspace-card-buttons workspace-card-buttons-compact">
          {/* Left side - icon buttons */}
          <div className="workspace-card-buttons-left">
            {canConnect && (!isAppStreamEnabled || env.isAppStreamConfigured) && (
              <Popup
                trigger={
                  <button
                    className={`workspace-action-btn-icon workspace-action-btn-primary ${connectionsButtonActive ? 'active' : ''}`}
                    onClick={this.handleToggle}
                  >
                    <Icon name="plug" style={iconStyle} />
                  </button>
                }
                content="Connections"
                size="small"
                position="top center"
                inverted
              />
            )}
            {!isAppStreamEnabled && state.canTerminate && !state.key.includes('FAILED') && (
              <Popup
                trigger={
                  <button
                    className={`workspace-action-btn-icon workspace-action-btn-secondary ${editCidrButtonActive ? 'active' : ''}`}
                    onClick={this.handleCidrEditToggle}
                  >
                    <Icon name="shield" style={iconStyle} />
                  </button>
                }
                content="Security Group"
                size="small"
                position="top center"
                inverted
              />
            )}
            {showDetailButton && (
              <Popup
                trigger={
                  <button
                    className="workspace-action-btn-icon workspace-action-btn-secondary"
                    onClick={this.handleViewDetail}
                  >
                    <Icon name="info circle" style={iconStyle} />
                  </button>
                }
                content="Details & Cost"
                size="small"
                position="top center"
                inverted
              />
            )}
            {enableEgressStore && state.canTerminate && !state.key.includes('FAILED') && (
              <Popup
                trigger={
                  <button
                    className={`workspace-action-btn-icon workspace-action-btn-secondary ${egressStoreButtonActive ? 'active' : ''}`}
                    onClick={this.handleEgressStoreToggle}
                  >
                    <Icon name="external share" style={iconStyle} />
                  </button>
                }
                content="Egress Store"
                size="small"
                position="top center"
                inverted
              />
            )}
          </div>

          {/* Right side - state buttons */}
          <div className="workspace-card-buttons-right">
            {(canChangeInstanceType || canModifyStorageSize || canSetSchedule) && (
              <div className="workspace-actions-dropdown-wrapper">
                <Popup
                  trigger={
                    <button
                      className="workspace-action-btn-icon workspace-action-btn-secondary"
                      data-testid="sc-env-actions-dropdown-compact"
                      disabled={processing}
                      onClick={(e) => { e.stopPropagation(); this.setActionsDropdownOpen(!this.actionsDropdownOpen); }}
                    >
                      <Icon name="setting" style={iconStyle} />
                    </button>
                  }
                  content="Actions"
                  size="small"
                  position="top center"
                  inverted
                  disabled={this.actionsDropdownOpen}
                />
                {this.actionsDropdownOpen && (
                  <>
                    <div className="workspace-actions-dropdown-backdrop" onClick={() => this.setActionsDropdownOpen(false)} />
                    <div className="workspace-actions-dropdown-menu">
                      {canChangeInstanceType && (
                        <button
                          className="workspace-actions-dropdown-item"
                          data-testid="sc-env-change-instance-type-compact"
                          onClick={() => { this.setActionsDropdownOpen(false); this.setChangeInstanceTypeModalOpen(true); }}
                        >
                          <Icon name="exchange" style={{ lineHeight: 1, height: '1em', margin: 0, color: '#2563eb' }} />
                          Change Instance Type
                        </button>
                      )}
                      {canModifyStorageSize && (
                        <button
                          className="workspace-actions-dropdown-item"
                          data-testid="sc-env-modify-storage-size-compact"
                          onClick={() => { this.setActionsDropdownOpen(false); this.setModifyStorageSizeModalOpen(true); }}
                        >
                          <Icon name="hdd" style={{ lineHeight: 1, height: '1em', margin: 0, color: '#2563eb' }} />
                          Modify Storage Size
                        </button>
                      )}
                      {canSetSchedule && this.renderSetScheduleDropdownItem('sc-env-set-schedule-compact')}
                    </div>
                  </>
                )}
              </div>
            )}
            {canStart && (
              <Popup
                trigger={
                  <button
                    className="workspace-action-btn-icon workspace-action-btn-start"
                    onClick={this.handleStart}
                    disabled={processing}
                  >
                    {processing ? <Icon name="spinner" loading style={iconStyle} /> : <Icon name="play" style={iconStyle} />}
                  </button>
                }
                content="Start"
                size="small"
                position="top center"
                inverted
              />
            )}
            {canRestart && (
              <Popup
                trigger={
                  <button
                    className="workspace-action-btn-icon workspace-action-btn-restart"
                    onClick={this.handleRestart}
                    disabled={processing}
                  >
                    {processing ? <Icon name="spinner" loading style={iconStyle} /> : <Icon name="redo" style={iconStyle} />}
                  </button>
                }
                content="Restart"
                size="small"
                position="top center"
                inverted
              />
            )}
            {canStop && (
              <Popup
                trigger={
                  <button
                    className="workspace-action-btn-icon workspace-action-btn-stop"
                    onClick={this.handleStop}
                    disabled={processing}
                  >
                    {processing ? <Icon name="spinner" loading style={iconStyle} /> : <Icon name="stop" style={iconStyle} />}
                  </button>
                }
                content="Stop"
                size="small"
                position="top center"
                inverted
              />
            )}
            {state.canTerminate && (
              <Popup
                trigger={
                  <button
                    className="workspace-action-btn-icon workspace-action-btn-terminate"
                    onClick={() => this.setTerminateModalState(true)}
                    disabled={processing || this.environment.terminationLocked}
                  >
                    {this.environment.terminationLocked ? <Icon name="lock" style={iconStyle} /> : <Icon name="trash alternate" style={iconStyle} />}
                  </button>
                }
                content={this.environment.terminationLocked ? "Locked" : "Terminate"}
                size="small"
                position="top center"
                inverted
              />
            )}
          </div>
        </div>
        
        {/* Terminate Modal - same as normal mode */}
        <Modal
          dimmer='blurring'
          basic
          size='large'
          open={this.terminateModalOpen}
          onClose={() => this.setTerminateModalState(false)}
        >
          <div className="workspace-modal-header">
            <Icon name='warning sign' color='red' size='massive' />
            <div className="workspace-modal-title">
              <span>Proceed with caution!</span>
            </div>
          </div>
          <Modal.Content>
            <div className="workspace-modal-content">
              Performing this action will permanently erase your workspace and all related data, which cannot be restored.
              Any data within a study will be retained. Are you sure you want to proceed?
            </div>
          </Modal.Content>
          <div className="workspace-modal-actions">
            <Modal.Actions>
              <button className="workspace-action-btn workspace-action-btn-start" onClick={() => this.setTerminateModalState(false)}>
                Cancel
              </button>
              <button className="workspace-action-btn workspace-action-btn-terminate" onClick={this.handleTerminate}>
                Terminate
              </button>
            </Modal.Actions>
          </div>
        </Modal>

        {enableEgressStore && egressStoreButtonActive && <ScEnvironmentEgressStoreDetail scEnvironment={env} />}
        {canConnect && connectionsButtonActive && <ScEnvironmentConnections scEnvironment={env} config={this.props.config} />}
        {editCidrButtonActive && <ScEnvironmentUpdateCidrs scEnvironment={env} onCancel={this.handleCidrEditToggle} />}
        <ChangeInstanceTypeModal
          open={this.changeInstanceTypeModalOpen}
          onClose={() => this.setChangeInstanceTypeModalOpen(false)}
          environment={env}
          onConfirm={this.handleChangeInstanceTypeConfirm}
          getCompatibleInstanceTypes={getScCompatibleInstanceTypes}
        />
        <ModifyStorageSizeModal
          open={this.modifyStorageSizeModalOpen}
          onClose={() => this.setModifyStorageSizeModalOpen(false)}
          environment={env}
          onConfirm={this.handleModifyStorageSizeConfirm}
          getStorageInfo={getScStorageInfo}
        />
        {this.renderSchedulingModal(env)}
      </>
    );
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(ScEnvironmentButtons, {
  envsStore: computed,
  environment: computed,
  userStore: computed,
  envTypesStore: computed,
  hasCidrParam: computed,
  envType: computed,
  canSetSchedule: computed,
  processing: observable,
  connectionsButtonActive: observable,
  egressStoreButtonActive: observable,
  editCidrButtonActive: observable,
  terminateModalOpen: observable,
  changeInstanceTypeModalOpen: observable,
  modifyStorageSizeModalOpen: observable,
  schedulingModalOpen: observable,
  actionsDropdownOpen: observable,
  handleViewDetail: action,
  handleAction: action,
  handleToggle: action,
  handleCidrEditToggle: action,
  handleEgressStoreToggle: action,
  setChangeInstanceTypeModalOpen: action,
  setModifyStorageSizeModalOpen: action,
  setSchedulingModalOpen: action,
  setActionsDropdownOpen: action,
  handleChangeInstanceTypeConfirm: action,
  handleModifyStorageSizeConfirm: action,
  handleSetScheduleClick: action,
  handleSaveScheduleConfirm: action,
  handleSaveIdleStopConfirm: action,
});

// eslint-disable-next-line import/no-mutable-exports
const exportable = inject('scEnvironmentsStore', 'envTypesStore', 'userStore')(withRouter(observer(ScEnvironmentButtons)));
export default exportable;
